import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef } from "react"

import { Button } from "@/components/common/Button"
import { TableHeaderActions } from "@/components/Tables/TableHeaderActions"
import type { InstructionSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import {
  CreateInstructionDialog,
  EditInstructionDialog,
} from "@/screens/ManageProducts/components/Instructions/components/dialogs"
import { FilterDrawer } from "@/screens/ManageProducts/components/Instructions/components/filter/FilterDrawer"
import InstructionFilterChips from "@/screens/ManageProducts/components/Instructions/components/filter/InstructionFilterChips"
import InstructionTable from "@/screens/ManageProducts/components/Instructions/components/tables/InstructionTable"
import { useInstructionDialog } from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionDialog"
import { useInstructionFilter } from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionFilter"
import useSearchList from "@/screens/ManageProducts/components/Instructions/hooks/useSearchList"
import { InstructionDialogs } from "@/screens/ManageProducts/components/Instructions/types"
import type { InstructionSortableAccessorKey } from "@/screens/ManageProducts/components/Instructions/utils/sorftFieldMap"
import {
  accessorKeyToInstructionSortField,
  instructionSortFieldToAccessoryKey,
} from "@/screens/ManageProducts/components/Instructions/utils/sorftFieldMap"
import { useInstructionSearchStore } from "@/screens/ManageProducts/stores/useInstructionSearchStore"

const InstructionContainer: FC = () => {
  const navigate = useNavigate()
  const { setDialogOpen, dialogOpen, selectedInstruction, setSelectedInstruction } = useInstructionDialog()
  const { filterDrawerOpen, setFilterDrawerOpen } = useInstructionFilter()
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { searchParams, data, fetching, error, refetchData, clearStore } = useSearchList()
  const { query, field, order, isActive } = searchParams

  const loadMore = useInstructionSearchStore(({ loadMore }) => loadMore)

  useEffect(() => {
    refetchData()
  }, [refetchData])

  const [throttledLoadMore] = useThrottle(() => {
    loadMore(queryClient, searchParams)
  }, 200)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = instructionSortFieldToAccessoryKey.get(field as InstructionSortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as InstructionSortableAccessorKey
      const field = accessorKeyToInstructionSortField.get(key) as InstructionSortField
      const params = {
        query,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
        isActive,
      }

      void navigate({
        search: params,
        to: "/app/admin/manage-products/instructions",
        replace: true,
      })
    }
  }

  const handleOnSuccess = async () => {
    await refetchData()

    handleDialogClose()
  }

  const handleDialogClose = () => {
    setSelectedInstruction(null)
    setDialogOpen(null)
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <FilterDrawer showFilterDrawer={filterDrawerOpen} toggleDrawer={setFilterDrawerOpen} />

      <TableHeaderActions currentSearch={searchParams} placeholder='Search by Instruction Name or Instruction...'>
        <Button
          appearance='outlined'
          value='Filter'
          startIcon={<FilterAltOutlined />}
          onClick={() => setFilterDrawerOpen(true)}
          rootElementName='button'
        />
        <Button
          startIcon={<AddOutlined />}
          appearance='outlined'
          onClick={() => setDialogOpen(InstructionDialogs.ADD_INSTRUCTION)}
        >
          Add Instruction
        </Button>
      </TableHeaderActions>

      <Box data-testid='instruction-chips' className='flex flex-wrap items-center gap-4'>
        <InstructionFilterChips />
      </Box>

      <Box className='grow overflow-hidden'>
        <InstructionTable
          data={data}
          scrollHandler={scrollHandler}
          tableContainerRef={tableContainerRef}
          handleSortingChange={handleSortingChange}
          setSelectedInstruction={setSelectedInstruction}
          setDialogOpen={setDialogOpen}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>
      {dialogOpen === InstructionDialogs.ADD_INSTRUCTION && (
        <CreateInstructionDialog isOpen={dialogOpen} onSuccess={handleOnSuccess} onClose={handleDialogClose} />
      )}
      {dialogOpen === InstructionDialogs.EDIT_INSTRUCTION && selectedInstruction && (
        <EditInstructionDialog
          isOpen={dialogOpen}
          instruction={selectedInstruction}
          onSuccess={handleOnSuccess}
          onClose={handleDialogClose}
        />
      )}
    </Box>
  )
}

export default InstructionContainer
